import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRefs, useContext, watch } from '@nuxtjs/composition-api';
import ERROR_MESSAGES from "../../../validators/error-messages";
import { useMobile } from '~/core/composables/useMobile';
import { emailExists } from '~/core/validators/email';
import { ACTIONS as EMAIL_VERIFICATION_ACTIONS, GETTERS as EMAIL_VERIFICATION_GETTERS } from '~/store/affility/user/flows/email-verification/names';

interface Props {
    visible: boolean,
    emailConfirmationExpiration: number | Date | string,
    isConfirmationRequired?: boolean,
    currentEmail: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RequireEmailConfirmation',
  props: {
    visible: { type: Boolean },
    emailConfirmationExpiration: null,
    isConfirmationRequired: { type: Boolean, default: false },
    currentEmail: null
  },
  setup(__props: any) {

const props = __props as { visible: boolean, emailConfirmationExpiration: number | Date | string, isConfirmationRequired: boolean, currentEmail: string };

const BACK_END_EMAIL_INTERVAL = 60000;

const { store } = useContext();
const isMobile = useMobile();
const buttonSize = computed(() => isMobile.value ? 's' : 'm');



const {
    visible,
    emailConfirmationExpiration,
    isConfirmationRequired,
    currentEmail,
} = toRefs(props);

const internalVisible = ref(visible.value);
const generalError = ref('');
const validating = ref(false);
const buttonActionInProgress = ref(false);
const email = ref('');
const emailError = ref('');
const lastEmailDate = computed(() => store.getters[`affility/user/flows/email-verification/${EMAIL_VERIFICATION_GETTERS.LAST_EMAIL_DATE}`]);
const lastSentRecently = ref(false);

const waitForValidation = async () => {
    return new Promise<void>((resolve) => {
        const unwatch = watch(validating, (value) => {
            if (!value) {
                unwatch();
                resolve();
            }
        });
    });
};

const callWithLoadingAndErrorHandling = (
    action: (...args: unknown[]) => Promise<void>,
    errorMessage?: string | ((e: Error) => string),
    considerValidation = true,
) => {
    return async (...args: unknown[]) => {
        buttonActionInProgress.value = true;
        generalError.value = '';
        try {
            if (considerValidation && validating.value) {
                const emailBeforeValidation = email.value;
                await waitForValidation();
                if (emailError.value ||
                    generalError.value ||
                    emailBeforeValidation !== email.value) {
                    return;
                }
            }
            await action(...args);
            lastSentRecently.value = true;
        } catch (error: unknown) {
            generalError.value = typeof errorMessage === 'function' ?
                errorMessage(error as Error) :
                errorMessage;
        } finally {
            buttonActionInProgress.value = false;
        }
    };
};

const checkAndReOpenFlow = () =>{    
    if (isConfirmationRequired) {
        return store.dispatch( `affility/user/flows/email-verification/${EMAIL_VERIFICATION_ACTIONS.START_EMAIL_VERIFICATION}`);
    }
};

const cancelVerification = store.dispatch.bind(
    null,
    `affility/user/flows/email-verification/${EMAIL_VERIFICATION_ACTIONS.CANCEL_EMAIL_VERIFICATION}`,
);

const resendEmail = callWithLoadingAndErrorHandling(
    async (arg) => {
        await store.dispatch(
            `affility/user/flows/email-verification/${EMAIL_VERIFICATION_ACTIONS.RESEND_LINK_MUTATE_USER_AND_SHOW_NOTIFICATION}`,
            arg,
        );
        checkAndReOpenFlow();
    },
    'Oops...Something went wrong. Please try again',
    false,
);

const changeEmail = callWithLoadingAndErrorHandling(
    async (arg) => {
        await store.dispatch(
            `affility/user/flows/email-verification/${EMAIL_VERIFICATION_ACTIONS.CHANGE_EMAIL_MUTATE_USER_AND_SHOW_NOTIFICATION}`,
            arg,
        );
        checkAndReOpenFlow();
    },
    'Oops...Something went wrong. Please try again',
    true,
);


watch(email, async (newValue) => {
    if (!internalVisible.value) {
        return;
    }
    generalError.value = '';
    emailError.value = '';
    try {
        validating.value = true;
        if(currentEmail.value.toLowerCase() === newValue.toLowerCase()){
            throw {message: ERROR_MESSAGES.EMAIL_ERR.LINKED};
        }
        await emailExists(
            null,
            newValue,
            (err?: Error) => { if (err) throw {message: err.message}; },
        );
    } catch (error: unknown) {
        emailError.value = (error as Error).message;
    } finally {
        if (newValue === email.value) { // guard for race conditions
            validating.value = false;
        }
    }
});
watch(visible, (value) => {
    internalVisible.value = value;
});
const updateLastSentRecently = (lastEmailDate: number) => {
    lastSentRecently.value = Date.now() - lastEmailDate < BACK_END_EMAIL_INTERVAL;
};
watch(lastEmailDate, updateLastSentRecently);
watch(internalVisible, (value) => {
    if (!value) {
        email.value = '';
        emailError.value = '';
        generalError.value = '';
        validating.value = false;
        cancelVerification({ logoutIfNecessary: false });
    } else {
        updateLastSentRecently(lastEmailDate.value);
    }
}, {immediate:true});

return { __sfc: true,BACK_END_EMAIL_INTERVAL, store, isMobile, buttonSize, props, visible, emailConfirmationExpiration, isConfirmationRequired, currentEmail, internalVisible, generalError, validating, buttonActionInProgress, email, emailError, lastEmailDate, lastSentRecently, waitForValidation, callWithLoadingAndErrorHandling, checkAndReOpenFlow, cancelVerification, resendEmail, changeEmail, updateLastSentRecently }
}

})